import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { cleanForFirestore } from '../utils/firestoreClean';
import { normalizeAnswers, normalizeAwarded, safeSetAssignee, safeSet } from '../utils/firestoreWrite';
import { validateQuestionsForGrading } from './aiService';

export interface Assignment {
  id?: string;
  quizId?: string; // Optional for backwards compatibility
  teacherId: string;
  schoolId: string;
  title: string;
  description?: string;
  questions?: any[]; // Store questions directly for AI-generated quizzes
  startAt: Date;
  endAt: Date;
  isActive: boolean;
  // ✅ SECURITY SETTINGS for anti-cheat/lockdown features
  security?: {
    fullscreenRequired?: boolean; // Enforce fullscreen mode
    preventTabSwitch?: boolean; // Detect tab switches
    preventCopyPaste?: boolean; // Block copy/paste on questions
  };
  createdAt?: any;
  updatedAt?: any;
  // Computed fields
  totalAssignees?: number;
  completedCount?: number;
}

export interface Assignee {
  assignmentId: string;
  studentId: string;
  displayName?: string; // Denormalized for TeacherMonitor (avoids N+1 reads)
  email?: string;       // Denormalized for TeacherMonitor (avoids N+1 reads)
  status: 'not_started' | 'in_progress' | 'paused' | 'submitted' | 'auto_submitted';
  // ❌ REMOVED: unlockCode (SECURITY FIX - never store plaintext)
  unlockHash?: string; // SHA-256 hash for server verification (optional, set when teacher issues code)
  unlockExpiresAt?: Date | null; // When unlock code expires (10 min validity)
  unlockIssuedAt?: Date | null; // When code was issued
  unlockIssuedBy?: string; // Teacher UID who issued the code
  unlockVerifiedAt?: Date | null; // When student successfully verified code
  progress: {
    startedAt?: Date;
    submittedAt?: Date;
    currentQuestionIndex?: number;
    timeSpent?: number; // seconds
    violationCount?: number;
    lastActivity?: Date;
    paused?: boolean; // Whether exam is currently paused
    pausedReason?: string | null; // Why exam was paused
  };
  // Answer submission fields - CRITICAL FOR SAVING STUDENT RESPONSES
  answers?: Record<string, any> | any[]; // Student answers - array or object supported
  awarded?: Record<string, number | null>; // Marks awarded by question/part ID
  totalScore?: number; // Total score achieved
  maxScore?: number; // Maximum possible score
  submittedAt?: Date; // When exam was submitted
  autoGraded?: boolean; // Whether auto-grading was applied
  gradingResult?: any; // Auto-grading results
  createdAt?: any;
  updatedAt?: any;
}

export interface AssignmentCreationParams {
  quizId?: string; // Optional for backwards compatibility
  questions?: any[]; // For AI-generated quizzes
  quizTitle?: string; // Original quiz title for reference
  teacherId: string;
  schoolId?: string;
  title: string;
  description?: string;
  studentIds: string[];
  startAt: Date;
  endAt: Date;
}

// ❌ REMOVED: Client-side unlock code generation (SECURITY FIX)
// Unlock codes are now generated server-side only via teacherIssueUnlockCode Cloud Function
// This prevents students from reading plaintext codes from Firestore

/**
 * Create a new assignment with per-student unlock codes
 */
export async function createAssignment(params: AssignmentCreationParams): Promise<string> {
  console.log('🚀 Creating assignment with params:', { 
    ...params, 
    questions: params.questions ? `${params.questions.length} questions` : 'none',
    studentIds: `${params.studentIds.length} students`,
    studentIdsList: params.studentIds
  });
  
  try {
    const {
      quizId,
      questions,
      quizTitle,
      teacherId,
      schoolId = 'default',
      title,
      description,
      studentIds,
      startAt,
      endAt
    } = params;

    console.log('✅ Parameters extracted successfully');

    if ((!quizId && !questions) || !teacherId || !title || studentIds.length === 0) {
      console.error('❌ Missing required parameters:', { 
        hasQuizId: !!quizId, 
        hasQuestions: !!questions, 
        hasTeacherId: !!teacherId, 
        hasTitle: !!title, 
        studentCount: studentIds.length 
      });
      throw new Error('Missing required assignment parameters');
    }

    if (endAt <= startAt) {
      console.error('❌ Invalid date range:', { startAt, endAt });
      throw new Error('End date must be after start date');
    }

    console.log('✅ Basic validation passed');

    // PR-GRADING-1: Validate questions before saving
    if (questions && questions.length > 0) {
      console.log('🔍 Validating questions for grading compatibility...');
      const validationErrors = validateQuestionsForGrading(questions);

      const criticalErrors = validationErrors.filter(e => e.severity === 'error');
      const warnings = validationErrors.filter(e => e.severity === 'warning');

      if (criticalErrors.length > 0) {
        console.error('❌ Question validation failed:', criticalErrors);
        const errorMessages = criticalErrors.map(e =>
          `Question ${e.questionIndex + 1} (${e.questionType}): ${e.issue}`
        ).join('\n');
        throw new Error(`Cannot create assignment - invalid questions:\n${errorMessages}`);
      }

      if (warnings.length > 0) {
        console.warn('⚠️ Question validation warnings:', warnings);
        // Warnings don't block creation, but are logged
      }

      console.log('✅ Question validation passed');
    }

    console.log('📄 Creating assignment document...');

    // Create the assignment document
    const assignmentData: Omit<Assignment, 'id'> = {
      ...(quizId && { quizId }),
      ...(questions && { questions }),
      ...(quizTitle && { quizTitle }),
      teacherId,
      schoolId,
      title,
      ...(description && { description }), // Only include description if it exists
      startAt,
      endAt,
      isActive: true,
      // ✅ DEFAULT SECURITY SETTINGS - Enable anti-cheat features by default
      security: {
        fullscreenRequired: true,    // Enforce fullscreen mode
        preventTabSwitch: true,       // Detect tab switches
        preventCopyPaste: true,       // Block copy/paste on questions

        // ANTI-CHEAT Phase 2: Randomization
        randomizeQuestions: true,     // Shuffle question order per student
        randomizeAnswers: false,       // DISABLED: Breaks grading until index mapping implemented

        // ANTI-CHEAT Phase 2: Time pressure (default disabled - teacher must enable)
        perQuestionTimeLimit: 0       // 0 = disabled, >0 = seconds per question
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log('📄 Assignment data prepared:', {
      hasQuizId: !!assignmentData.quizId,
      hasQuestions: !!assignmentData.questions,
      questionsCount: assignmentData.questions?.length,
      teacherId: assignmentData.teacherId,
      title: assignmentData.title
    });

    console.log('📚 Adding document to Firestore...');
    const assignmentRef = await addDoc(collection(db, 'assignments'), assignmentData);
    const assignmentId = assignmentRef.id;
    console.log('✅ Assignment document created with ID:', assignmentId);

    console.log('👥 Creating assignee documents for students...');

    // ✅ SECURITY FIX: No unlock codes stored on creation
    // Teachers will generate codes on-demand via Cloud Function
    const assigneePromises = studentIds.map(async (studentId, index) => {
      console.log(`📝 Processing student ${index + 1}/${studentIds.length}: ${studentId}`);

      // Denormalize display info so TeacherMonitor avoids N+1 getDoc reads per snapshot
      let studentDisplayName = '';
      let studentEmail = '';
      try {
        const studentDocSnap = await getDoc(doc(db, 'users', studentId));
        if (studentDocSnap.exists()) {
          const sd = studentDocSnap.data() as any;
          studentDisplayName = sd.displayName || sd.email || '';
          studentEmail = sd.email || '';
        }
      } catch {
        // Non-fatal: TeacherMonitor will show 'Unknown' for this student
      }

      const assigneeData: Omit<Assignee, 'assignmentId'> = {
        studentId,
        displayName: studentDisplayName,
        email: studentEmail,
        status: 'not_started',
        // ❌ REMOVED: unlockCode, unlockHash (now generated server-side on-demand)
        unlockExpiresAt: null,
        unlockIssuedAt: null,
        unlockVerifiedAt: null,
        progress: {
          paused: false,
          pausedReason: null
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        assignmentId // Include for consistency
      };

      console.log(`📄 Creating assignee document for ${studentId}...`);
      const assigneeRef = doc(db, 'assignments', assignmentId, 'assignees', studentId);
      await setDoc(assigneeRef, assigneeData);
      console.log(`✅ Assignee document created for ${studentId}`);
    });

    console.log('⏳ Waiting for all assignee documents to be created...');
    await Promise.all(assigneePromises);
    console.log('✅ All assignee documents created successfully');

    // Initialize control document for live monitoring
    try {
      const controlRef = doc(db, 'assignments', assignmentId, 'control', 'commands');
      await setDoc(controlRef, {
        pauseAll: false,
        unlockAll: false,
        broadcast: null,
        lastCommand: null,
        createdAt: serverTimestamp()
      });
      console.log('✅ Control document initialized');
    } catch (controlError) {
      console.warn('⚠️ Failed to initialize control document:', controlError);
      // Don't fail assignment creation if control doc fails
    }

    console.log(`✅ Assignment created: ${assignmentId} for ${studentIds.length} students`);
    return assignmentId;

  } catch (error) {
    console.error('❌ Error creating assignment:', error);
    console.error('❌ Error details:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    
    // Preserve the original error message for better debugging
    const errorMessage = error.message || error.code || 'Unknown error occurred';
    throw new Error(`Failed to create assignment: ${errorMessage}`);
  }
}

/**
 * Get assignments for a specific student using a server-side approach
 * This works by calling a cloud function that can query across collections
 */
export async function listAssignmentsForStudent(studentId: string): Promise<Assignment[]> {
  console.log('🔍 listAssignmentsForStudent called with studentId:', studentId);
  
  try {
    // Try to use a cloud function to get student assignments
    console.log('☁️ Attempting to call cloud function for student assignments...');
    
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('../firebase');
    
    try {
      const getStudentAssignments = httpsCallable(functions, 'getStudentAssignments');
      const result = await getStudentAssignments({ studentId });
      const assignments = (result.data as any).assignments || [];
      
      console.log('✅ Cloud function returned assignments:', assignments.length);
      return assignments;
      
    } catch (cloudFunctionError) {
      console.warn('⚠️ Cloud function failed:', cloudFunctionError);
      console.warn('⚠️ Error details:', {
        name: cloudFunctionError.name,
        message: cloudFunctionError.message,
        code: cloudFunctionError.code
      });
      
      // Fallback: Return a mock assignment for testing if in development
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Development mode: providing test assignment');
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        
        return [{
          id: 'test-assignment-' + studentId,
          title: 'Test Assignment (Development Mode)',
          description: 'This is a test assignment visible in development mode',
          questions: [
            {
              id: '1',
              type: 'mcq',
              text: 'What is 2 + 2?',
              options: ['3', '4', '5', '6'],
              correctAnswer: 1
            }
          ],
          teacherId: 'test-teacher',
          schoolId: 'default',
          startAt: now,
          endAt: tomorrow,
          isActive: true,
          createdAt: now,
          updatedAt: now
        }];
      }
      
      // Production: return empty array
      console.log('📝 No cloud function available and not in development mode');
      return [];
    }

  } catch (error) {
    console.error('❌ Error fetching student assignments:', error);
    console.log('🔄 Returning empty array due to error');
    return [];
  }
}

/**
 * Get assignments created by a teacher
 */
export async function listAssignmentsByTeacher(teacherId: string): Promise<Assignment[]> {
  console.log('🔍 listAssignmentsByTeacher called with teacherId:', teacherId);
  
  try {
    // Check if Firebase is properly initialized
    if (!db) {
      console.error('❌ Firebase db is not initialized');
      throw new Error('Database not initialized');
    }
    
    console.log('📚 Attempting to query assignments collection...');
    
    // Start with the simplest possible query first
    const q = query(
      collection(db, 'assignments'),
      where('teacherId', '==', teacherId)
    );

    console.log('🔍 Query created, executing getDocs...');
    const querySnapshot = await getDocs(q);
    
    console.log('✅ Query executed successfully, docs found:', querySnapshot.size);
    
    if (querySnapshot.empty) {
      console.log('📝 No assignments found for teacher:', teacherId);
      return [];
    }
    
    const assignments = querySnapshot.docs.map(doc => {
      const data = doc.data();
      console.log('📄 Assignment doc:', doc.id, data);
      return {
        id: doc.id,
        ...data
      };
    }) as Assignment[];

    // Sort manually by createdAt (newest first)
    const sortedAssignments = assignments.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    });

    console.log('✅ Assignments loaded and sorted:', sortedAssignments.length);
    return sortedAssignments;

  } catch (error) {
    console.error('❌ Error in listAssignmentsByTeacher:', error);
    console.error('❌ Error details:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    
    // Return empty array instead of throwing to prevent UI crashes
    console.log('🔄 Returning empty array due to error');
    return [];
  }
}

/**
 * Get assignment details with assignee information
 */
export async function getAssignmentWithAssignees(assignmentId: string): Promise<{
  assignment: Assignment | null;
  assignees: Assignee[];
}> {
  try {
    // Get assignment
    const assignmentRef = doc(db, 'assignments', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);

    if (!assignmentSnap.exists()) {
      return { assignment: null, assignees: [] };
    }

    const assignment: Assignment = {
      id: assignmentSnap.id,
      ...assignmentSnap.data()
    } as Assignment;

    // Get all assignees
    const assigneesRef = collection(db, 'assignments', assignmentId, 'assignees');
    const assigneesSnap = await getDocs(assigneesRef);
    
    const assignees: Assignee[] = assigneesSnap.docs.map(doc => ({
      assignmentId,
      ...doc.data()
    })) as Assignee[];

    return { assignment, assignees };

  } catch (error) {
    console.error('Error fetching assignment details:', error);
    throw new Error('Failed to load assignment details');
  }
}

/**
 * Get assignment for a specific student
 */
export async function getStudentAssignment(assignmentId: string, studentId: string): Promise<{
  assignment: Assignment | null;
  assignee: Assignee | null;
}> {
  try {
    // Get assignment
    const assignmentRef = doc(db, 'assignments', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);

    if (!assignmentSnap.exists()) {
      return { assignment: null, assignee: null };
    }

    const assignment: Assignment = {
      id: assignmentSnap.id,
      ...assignmentSnap.data()
    } as Assignment;

    // Get student's assignee record
    const assigneeRef = doc(db, 'assignments', assignmentId, 'assignees', studentId);
    const assigneeSnap = await getDoc(assigneeRef);

    const assignee: Assignee | null = assigneeSnap.exists() ? {
      assignmentId,
      ...assigneeSnap.data()
    } as Assignee : null;

    return { assignment, assignee };

  } catch (error) {
    console.error('Error fetching student assignment:', error);
    throw new Error('Failed to load assignment');
  }
}

/**
 * Update assignee status and progress
 */
/**
 * Create-or-update assignee doc with safe, cleaned data using the safe wrapper.
 */
export async function updateAssigneeProgress(
  assignmentId: string,
  studentId: string,
  updates: Partial<Assignee>
): Promise<void> {
  const ref = doc(db, 'assignments', assignmentId, 'assignees', studentId);

  const payload: Partial<Assignee> = {
    ...updates,
    answers: normalizeAnswers(updates.answers),
    awarded: normalizeAwarded(updates.awarded),
  };

  // Use safe wrapper that deep-cleans + creates-or-updates
  await safeSetAssignee(ref, payload);
}

/**
 * Update assignment details
 */
export async function updateAssignment(
  assignmentId: string, 
  updates: Partial<Pick<Assignment, 'title' | 'description' | 'startAt' | 'endAt' | 'questions'>>
): Promise<void> {
  try {
    const assignmentRef = doc(db, 'assignments', assignmentId);
    await updateDoc(assignmentRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating assignment:', error);
    throw new Error('Failed to update assignment');
  }
}

/**
 * Delete an assignment and all its assignees
 */
export async function deleteAssignment(assignmentId: string): Promise<void> {
  try {
    // First delete all assignee subcollection documents
    const assigneesRef = collection(db, 'assignments', assignmentId, 'assignees');
    const assigneesSnapshot = await getDocs(assigneesRef);
    
    const deletePromises = assigneesSnapshot.docs.map(assigneeDoc => 
      deleteDoc(assigneeDoc.ref)
    );
    await Promise.all(deletePromises);
    
    // Then delete the assignment document
    const assignmentRef = doc(db, 'assignments', assignmentId);
    await deleteDoc(assignmentRef);
    
  } catch (error) {
    console.error('Error deleting assignment:', error);
    throw new Error('Failed to delete assignment');
  }
}

/**
 * Delete a student's completed assignment (student-specific)
 */
export async function deleteStudentAssignment(assignmentId: string, studentId: string): Promise<void> {
  try {
    console.log('🗑️ deleteStudentAssignment called:', { assignmentId, studentId });
    
    // Only delete the assignee record, not the entire assignment
    const assigneeRef = doc(db, 'assignments', assignmentId, 'assignees', studentId);
    console.log('📄 Attempting to delete assignee document...');
    
    await deleteDoc(assigneeRef);
    console.log('✅ Assignee document deleted successfully');
    
  } catch (error) {
    console.error('❌ Error deleting student assignment:', error);
    console.error('❌ Error details:', {
      name: error.name,
      message: error.message,
      code: error.code,
      assignmentId,
      studentId
    });
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. You can only delete completed assignments.');
    } else if (error.code === 'not-found') {
      throw new Error('Assignment not found or already deleted.');
    } else {
      throw new Error(error.message || 'Failed to delete assignment');
    }
  }
}

/**
 * Deactivate an assignment
 */
export async function deactivateAssignment(assignmentId: string): Promise<void> {
  try {
    const assignmentRef = doc(db, 'assignments', assignmentId);
    await updateDoc(assignmentRef, {
      isActive: false,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deactivating assignment:', error);
    throw new Error('Failed to deactivate assignment');
  }
}

/**
 * Enhanced submission with race-free safe writes
 */
export async function saveSubmission(
  assignmentId: string,
  studentId: string,
  payload: Partial<Assignee>
) {
  const ref = doc(db, 'assignments', assignmentId, 'assignees', studentId);
  await safeSet(ref, {
    ...payload,
    answers: normalizeAnswers(payload.answers),
    awarded: normalizeAwarded(payload.awarded),
    status: payload.status ?? 'submitted',
    submittedAt: payload.submittedAt ?? new Date()
  });
}

/**
 * Auto-grading writer (call after computing marks)
 */
export async function saveAutoGraded(
  assignmentId: string,
  studentId: string,
  rawAnswers: any,
  graded: { awarded?: Record<string, number>, total?: number, max?: number, details?: any }
) {
  await saveSubmission(assignmentId, studentId, {
    answers: rawAnswers,
    awarded: graded.awarded ?? {},
    totalScore: Number.isFinite(graded.total ?? NaN) ? graded.total : 0,
    maxScore: Number.isFinite(graded.max ?? NaN) ? graded.max : 0,
    autoGraded: true,
    gradingResult: graded.details ?? null
  });
}